import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowUp, ArrowDown, Save, X, Edit, Search, Eye, Trash2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import ReactECharts from 'echarts-for-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { showNotification, showError } from '../../utils/notificationHandler';
import debounce from 'lodash/debounce';
import PageHeader from '../shared/PageHeader';

const ORDER_STATUSES = [
  'Pending',
  'In Progress',
  'Invoiced',
  'Complete',
  'Cancelled',
  'On Hold',
  'On Order',
];

const SHIPPING_STATUSES = [
  'Waiting For Collection',
  'Shipped',
  'Delayed',
];

const OrderManagement = () => {
  // ?search=<order_number> (e.g. from the admin dashboard) pre-fills the search
  const initialSearch = new URLSearchParams(useLocation().search).get('search') || '';
  const [orders, setOrders] = useState([]);
  const [chartType, setChartType] = useState('Order Status');
  const [timeRange, setTimeRange] = useState('All Time');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterCompany, setFilterCompany] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [limit, setLimit] = useState(30);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchAccounts();
  }, [timeRange, sortField, sortDirection, filterCompany, debouncedSearchTerm, limit]);

  const fetchAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, company_name')
        .order('company_name');

      if (error) throw error;
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showError(error, 'Failed to fetch accounts');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('orders')
        .select(
          `
          id,
          order_id,
          order_number,
          status,
          external_status,
          synced_from_erp,
          created_at,
          cart_items,
          user_id,
          notes,
          mhs_reference,
          shipping_status,
          connote,
          approved_by,
          approved_at,
          invoice_status,
          invoice_number,
          invoice_date,
          is_invoiced,
          accounts:user_id (
            id,
            company_name,
            mhs_account_number
          ),
          approver:approved_by (
            id,
            full_name
          )
        `,
          { count: 'exact' }
        )
        .order(sortField, { ascending: sortDirection === 'asc' })
        .limit(limit);

      if (filterCompany) {
        query = query.eq('accounts.company_name', filterCompany);
      }

      if (debouncedSearchTerm) {
        query = query.or(
          `order_number.ilike.%${debouncedSearchTerm}%,mhs_reference.ilike.%${debouncedSearchTerm}%,order_id.ilike.%${debouncedSearchTerm}%`
        );
      }

      if (timeRange === 'Current Month') {
        const startDate = startOfMonth(new Date());
        const endDate = endOfMonth(new Date());
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      } else if (timeRange === 'Last Month') {
        const lastMonthDate = new Date();
        lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
        const startDate = startOfMonth(lastMonthDate);
        const endDate = endOfMonth(lastMonthDate);
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setOrders(data || []);
      setTotalOrders(count || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders. Please try again.');
      showError(error, 'Failed to fetch orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder({ ...order });
    setIsEditModalOpen(true);
  };

  const handleViewOrder = (order) => {
    setViewingOrder({ ...order });
    setIsViewModalOpen(true);
  };

  const handleSaveOrder = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({
          status: editingOrder.status,
          notes: editingOrder.notes,
          mhs_reference: editingOrder.mhs_reference,
          shipping_status: editingOrder.shipping_status,
          connote: editingOrder.connote,
        })
        .eq('id', editingOrder.id)
        .select(
          `
          id,
          order_number,
          status,
          created_at,
          cart_items,
          user_id,
          notes,
          mhs_reference,
          shipping_status,
          connote,
          accounts:user_id (
            id,
            company_name,
            mhs_account_number
          )
        `
        );

      if (error) throw error;

      // Create a notification for the customer
      await supabase.from('notifications').insert({
        user_id: editingOrder.user_id,
        message: `Order #${editingOrder.order_number} has been updated. New status: ${editingOrder.status}`,
        read: false,
      });

      setOrders(
        orders.map((order) => (order.id === editingOrder.id ? data[0] : order))
      );
      setEditingOrder(null);
      setIsEditModalOpen(false);
      showNotification('Order updated successfully', 'success');
    } catch (error) {
      console.error('Error updating order:', error);
      showError(error, 'Failed to update order');
    }
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setIsEditModalOpen(false);
  };

  const handleCloseView = () => {
    setViewingOrder(null);
    setIsViewModalOpen(false);
  };

  const handleDeleteOrder = (order) => {
    setDeletingOrder(order);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingOrder) return;

    setDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Use the database function to delete the order
      const { data: functionResult, error: functionError } = await supabase
        .rpc('delete_order', {
          target_order_id: deletingOrder.id,
          staff_user_id: user.id
        });

      if (functionError) throw functionError;

      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Failed to delete order');
      }

      showNotification(`Order #${functionResult.order_number} deleted successfully`, 'success');
      setIsDeleteModalOpen(false);
      setDeletingOrder(null);
      fetchOrders(); // Refresh the orders list

    } catch (error) {
      console.error('Error deleting order:', error);
      showError(error, 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeletingOrder(null);
    setIsDeleteModalOpen(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getChartOption = () => {
    if (chartType === 'Order Status') {
      return {
        title: {
          text: 'Order Status Distribution',
          left: 'center',
          textStyle: { color: '#374151', fontSize: 16 },
        },
        tooltip: {
          trigger: 'item',
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          textStyle: { color: '#374151' },
        },
        // Explicitly hide axes for pie chart
        xAxis: {
          show: false
        },
        yAxis: {
          show: false
        },
        series: [
          {
            name: 'Order Status',
            type: 'pie',
            radius: '50%',
            data: ORDER_STATUSES.map((status) => ({
              value: orders.filter((order) => order.status === status).length,
              name: status,
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };
    } else if (chartType === 'Number of Orders') {
      const ordersByDate = {};
      orders.forEach((order) => {
        const date = format(new Date(order.created_at), 'yyyy-MM-dd');
        if (ordersByDate[date]) {
          ordersByDate[date] += 1;
        } else {
          ordersByDate[date] = 1;
        }
      });

      const dates = Object.keys(ordersByDate).sort();

      return {
        title: {
          text: 'Number of Orders Over Time',
          left: 'center',
          textStyle: { color: '#374151', fontSize: 16 },
        },
        tooltip: {
          trigger: 'axis',
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: { color: '#374151' },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#374151' },
        },
        series: [
          {
            data: dates.map((date) => ordersByDate[date]),
            type: 'line',
            smooth: true,
            itemStyle: {
              color: '#fed402',
            },
          },
        ],
      };
    }
  };

  const loadMoreOrders = () => {
    setLimit(limit + 30);
  };

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSetSearchTerm(e.target.value);
  };

  // Debug: Log table dimensions on mount and when orders change
  useEffect(() => {
    const logTableDimensions = () => {
      const table = document.querySelector('.orders-table-container table');
      const container = document.querySelector('.orders-table-container');
      if (table && container) {
        console.log('[OrderManagement] Table dimensions:', {
          tableWidth: table.offsetWidth,
          containerWidth: container.offsetWidth,
          viewportWidth: window.innerWidth,
          isOverflowing: table.offsetWidth > container.offsetWidth,
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth
        });
      }
    };
    
    // Log after render
    setTimeout(logTableDimensions, 100);
  }, [orders]);

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <PageHeader
        title="Orders"
        description="View and manage all orders, update statuses, and track shipments"
      />

      {/* Chart Section */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          {/* Chart Type Selection */}
          <div className="mb-6 flex space-x-4 justify-center">
            <button
              onClick={() => setChartType('Order Status')}
              className={`py-2 px-4 rounded-md font-medium transition-colors ${
                chartType === 'Order Status'
                  ? 'bg-brand text-black'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Order Status
            </button>
            <button
              onClick={() => setChartType('Number of Orders')}
              className={`py-2 px-4 rounded-md font-medium transition-colors ${
                chartType === 'Number of Orders'
                  ? 'bg-brand text-black'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Number of Orders
            </button>
          </div>

          {/* Chart */}
          <div className="h-96">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
              </div>
            ) : (
              <ReactECharts option={getChartOption()} style={{ height: '100%' }} />
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Orders
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Order ID, Number or MHS Reference"
                value={searchTerm}
                onChange={handleSearchChange}
                className="block w-full py-2 px-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
              />
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Range
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
            >
              <option value="All Time">All Time</option>
              <option value="Current Month">Current Month</option>
              <option value="Last Month">Last Month</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Company
            </label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
            >
              <option value="">All Companies</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.company_name}>
                  {account.company_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full overflow-hidden">
        <div className="overflow-x-auto orders-table-container w-full">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
              <span className="ml-3 text-lg font-medium text-gray-600">Loading orders...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 font-medium">{error}</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No orders available.</p>
            </div>
          ) : (
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                  <th
                    onClick={() => handleSort('order_id')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  >
                    Order ID
                    {sortField === 'order_id' &&
                      (sortDirection === 'asc' ? (
                        <ArrowUp className="inline ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="inline ml-1 h-4 w-4" />
                      ))}
                  </th>
                  <th
                    onClick={() => handleSort('order_number')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  >
                    Order #
                    {sortField === 'order_number' &&
                      (sortDirection === 'asc' ? (
                        <ArrowUp className="inline ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="inline ml-1 h-4 w-4" />
                      ))}
                  </th>
                  <th
                    onClick={() => handleSort('created_at')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  >
                    Date
                    {sortField === 'created_at' &&
                      (sortDirection === 'asc' ? (
                        <ArrowUp className="inline ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="inline ml-1 h-4 w-4" />
                      ))}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Company
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Total
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  >
                    Status
                    {sortField === 'status' &&
                      (sortDirection === 'asc' ? (
                        <ArrowUp className="inline ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="inline ml-1 h-4 w-4" />
                      ))}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Approved By
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View order details"
                          aria-label="View order details"
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit order"
                          aria-label="Edit order"
                        >
                          <Edit className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete order"
                          aria-label="Delete order"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.order_id}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.order_number}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(order.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate" title={order.accounts?.company_name || '-'}>
                      {order.accounts?.company_name || '-'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${order.cart_items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {order.synced_from_erp ? (
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            order.status === 'Complete' ? 'bg-green-100 text-green-800' :
                            order.status === 'Invoiced' ? 'bg-purple-100 text-purple-800' :
                            order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {order.status}
                          </span>
                          {order.is_invoiced && order.invoice_number && (
                            <span className="text-xs text-purple-600">
                              INV: {order.invoice_number}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                            {order.external_status}
                          </span>
                        </div>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          order.status === 'Complete' ? 'bg-green-100 text-green-800' :
                          order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {order.status}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate" title={order.approver?.full_name || (order.approved_by ? 'Unknown Staff' : 'Pending')}>
                      {order.approver?.full_name || (order.approved_by ? 'Unknown Staff' : 'Pending')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Load More Button */}
      {orders.length < totalOrders && (
        <div className="flex justify-center">
          <button
            onClick={loadMoreOrders}
            className="bg-brand hover:bg-brand-hover text-black font-medium py-2 px-4 rounded-md transition-colors"
          >
            Load More ({orders.length} of {totalOrders})
          </button>
        </div>
      )}

      {/* View Order Modal */}
      {isViewModalOpen && viewingOrder && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleCloseView}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Order #{viewingOrder.order_number} - Details
                  </h3>
                  <button
                    onClick={handleCloseView}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close order details"
                  >
                    <X className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Order Information</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div><strong>Order ID:</strong> {viewingOrder.order_id}</div>
                        <div><strong>Order Number:</strong> {viewingOrder.order_number}</div>
                        <div><strong>Date:</strong> {format(new Date(viewingOrder.created_at), 'PPP')}</div>
                        <div><strong>Status:</strong> {viewingOrder.status}</div>
                        <div><strong>Shipping Status:</strong> {viewingOrder.shipping_status || 'Not Set'}</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div><strong>Company:</strong> {viewingOrder.accounts?.company_name || 'N/A'}</div>
                        <div><strong>Account Number:</strong> {viewingOrder.accounts?.mhs_account_number || 'N/A'}</div>
                        <div><strong>Approved By:</strong> {viewingOrder.approver?.full_name || (viewingOrder.approved_by ? 'Unknown Staff' : 'Pending')}</div>
                        {viewingOrder.approved_at && (
                          <div><strong>Approved At:</strong> {format(new Date(viewingOrder.approved_at), 'PPP')}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {viewingOrder.mhs_reference && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">MHS Reference</h4>
                        <p className="text-sm text-gray-600">{viewingOrder.mhs_reference}</p>
                      </div>
                    )}
                    {viewingOrder.connote && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">ConNote</h4>
                        <p className="text-sm text-gray-600">{viewingOrder.connote}</p>
                      </div>
                    )}
                    {viewingOrder.notes && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600">{viewingOrder.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Order Items */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">Order Items</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {viewingOrder.cart_items?.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-900">{item.partNumber}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.description}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-center">{item.quantity}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                ${parseFloat(item.price || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                ${(parseFloat(item.price || 0) * parseInt(item.quantity || 0)).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50">
                            <td colSpan="4" className="px-3 py-2 text-sm font-medium text-gray-900 text-right">Order Total:</td>
                            <td className="px-3 py-2 text-sm font-bold text-gray-900 text-right">
                              ${viewingOrder.cart_items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0)), 0).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:w-auto sm:text-sm"
                  onClick={handleCloseView}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {isEditModalOpen && editingOrder && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Edit Order</h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={editingOrder.status}
                      onChange={(e) => setEditingOrder({ ...editingOrder, status: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      {ORDER_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shipping Status</label>
                    <select
                      value={editingOrder.shipping_status}
                      onChange={(e) => setEditingOrder({ ...editingOrder, shipping_status: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      {SHIPPING_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">MHS Reference</label>
                    <input
                      type="text"
                      value={editingOrder.mhs_reference || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, mhs_reference: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ConNote</label>
                    <input
                      type="text"
                      value={editingOrder.connote || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, connote: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      value={editingOrder.notes || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-black hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleSaveOrder}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingOrder && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Order
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to permanently delete this order? This action cannot be undone.
                      </p>
                      <div className="mt-3 text-sm text-gray-700">
                        <p><strong>Order:</strong> #{deletingOrder.order_number}</p>
                        <p><strong>Customer:</strong> {deletingOrder.accounts?.company_name}</p>
                        <p><strong>Status:</strong> {deletingOrder.status}</p>
                        <p><strong>Total:</strong> ${deletingOrder.cart_items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Order'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={cancelDelete}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
